/**
 * Admin LLM Benchmarks page (Layer 2 viewer).
 *
 * Read-only surface that lists past 4×4×N benchmark runs from disk and
 * renders the markdown reports for each fixture in the run. Gated to
 * elevated platform roles via the backend's _require_elevated check.
 *
 * v1: list runs → pick a run → tabs per report (summary + per-fixture).
 * v2 ideas (later): inline diff between two runs, "Run again" button,
 *   sparkline of pass rate over time.
 */
import { useEffect, useState } from 'react';
import { CalendarClock, FileText, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useBenchmarkRuns,
  useBenchmarkReport,
  useBenchmarkReportData,
} from '@/hooks/useLLMBenchmarks';
import type { BenchmarkRun } from '@/services/llmBenchmarkService';
import { BenchmarkCharts } from '@/components/admin/BenchmarkCharts';

function formatRunTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

function prettySlug(slug: string): string {
  if (slug === 'summary') return 'Summary';
  return slug
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function LLMBenchmarksPage() {
  const runs = useBenchmarkRuns();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedRunId && runs.data?.runs?.length) {
      setSelectedRunId(runs.data.runs[0].run_id);
    }
  }, [runs.data, selectedRunId]);

  const selectedRun: BenchmarkRun | undefined = runs.data?.runs.find(
    (r) => r.run_id === selectedRunId,
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-coral" />
            LLM Benchmarks (Layer 2)
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Cross-config benchmark reports for the Google Ads keyword research
            agents. Each run compares N model configs across N agents × N
            fixtures. Methodology:{' '}
            <code className="rounded bg-warm-bg px-1 py-0.5 text-xs">
              ai-docs/google-ads/benchmark-methodology.md
            </code>
            .
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => runs.refetch()}
          disabled={runs.isFetching}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${runs.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-4 w-4 text-teal" />
              Runs
            </CardTitle>
            <CardDescription>
              Newest first. Re-runs are triggered via the CLI (
              <code className="rounded bg-warm-bg px-1 text-xs">
                scripts/benchmark_llm_configs.py
              </code>
              ).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {runs.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : runs.isError ? (
              <div className="text-sm text-destructive">
                Failed to load runs. {String(runs.error)}
              </div>
            ) : !runs.data?.runs.length ? (
              <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No benchmark runs found. Run the CLI on the dev box to
                produce one.
              </div>
            ) : (
              runs.data.runs.map((run) => (
                <RunRow
                  key={run.run_id}
                  run={run}
                  active={run.run_id === selectedRunId}
                  onSelect={() => setSelectedRunId(run.run_id)}
                />
              ))
            )}
          </CardContent>
        </Card>

        <div>
          {selectedRun ? (
            <RunDetail run={selectedRun} />
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-sm text-muted-foreground">
                Pick a run from the list to view its reports.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function RunRow({
  run,
  active,
  onSelect,
}: {
  run: BenchmarkRun;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
        active
          ? 'border-coral bg-coral-light text-coral-dark'
          : 'border-border hover:bg-warm-bg'
      }`}
    >
      <div className="font-medium tabular-nums">{run.run_id}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">
        {formatRunTimestamp(run.timestamp_utc)}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        <Badge variant="secondary" className="text-xs">
          {run.report_count} {run.report_count === 1 ? 'report' : 'reports'}
        </Badge>
        {run.has_summary && (
          <Badge className="bg-teal-light text-teal-dark text-xs">summary</Badge>
        )}
      </div>
    </button>
  );
}

function RunDetail({ run }: { run: BenchmarkRun }) {
  const reportSlugs = run.reports.map((r) => r.slug);
  const initialTab = reportSlugs[0] ?? '';
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (!reportSlugs.includes(tab)) {
      setTab(initialTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.run_id]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4 text-coral" />
          Run {run.run_id}
        </CardTitle>
        <CardDescription>
          {formatRunTimestamp(run.timestamp_utc)} · {run.report_count}{' '}
          {run.report_count === 1 ? 'report' : 'reports'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap h-auto justify-start">
            {run.reports.map((r) => (
              <TabsTrigger key={r.slug} value={r.slug}>
                {prettySlug(r.slug)}
              </TabsTrigger>
            ))}
          </TabsList>
          {run.reports.map((r) => (
            <TabsContent key={r.slug} value={r.slug} className="mt-4">
              <ReportViewer runId={run.run_id} slug={r.slug} />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}

function ReportViewer({ runId, slug }: { runId: string; slug: string }) {
  const report = useBenchmarkReport(runId, slug);

  if (report.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading report…
      </div>
    );
  }
  if (report.isError) {
    return (
      <div className="text-sm text-destructive">
        Failed to load report. {String(report.error)}
      </div>
    );
  }
  if (!report.data) return null;

  // Charts go above the markdown for per-fixture tabs. Summary tab is
  // hand-authored markdown with no per-fixture data — we skip charts there.
  const isSummaryTab = slug === 'summary';

  return (
    <div className="space-y-6">
      {!isSummaryTab && <ChartsSubview runId={runId} slug={slug} />}
      <div className="prose prose-sm max-w-none dark:prose-invert">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {report.data.markdown}
        </ReactMarkdown>
      </div>
    </div>
  );
}

function ChartsSubview({ runId, slug }: { runId: string; slug: string }) {
  const dataQuery = useBenchmarkReportData(runId, slug);

  if (dataQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading charts…
      </div>
    );
  }

  // 404 = older run with no JSON sidecar; graceful skip.
  const status = (dataQuery.error as { response?: { status?: number } })
    ?.response?.status;
  if (dataQuery.isError && status === 404) {
    return (
      <div className="rounded border border-dashed border-border p-3 text-xs text-muted-foreground">
        Charts unavailable for this run. Older runs were produced before the
        chart sidecar landed — markdown only below. Re-run the benchmark to
        emit the sidecar.
      </div>
    );
  }
  if (dataQuery.isError) {
    return (
      <div className="text-xs text-destructive">
        Failed to load chart data. {String((dataQuery.error as Error)?.message ?? '')}
      </div>
    );
  }
  if (!dataQuery.data) return null;
  return <BenchmarkCharts data={dataQuery.data} />;
}
